import * as React from 'react'

import { Copy } from '@mondough/copy'
import { LiveRegionCodesAlpha3, useLocale } from '@mondough/locale'
import { Button, Text } from '@mondough/monzo-ui'

import AppStoreButton from '../AppStoreButton/AppStoreButton'
import styles from './Footer.module.scss'
import { FooterProps } from './Footer.props'

const MONZO_COM = 'https://monzo.com'

function FooterLinks({
  privacyHref,
  termsHref,
}: {
  privacyHref: string
  termsHref: string
}) {
  return (
    <section className={styles.links}>
      <Button href={privacyHref} theme="invisible" className={styles.link}>
        <Copy.Simple
          id="app_tabs_monzo_me.footer.privacy_notice"
          fallback="Privacy notice"
        />
      </Button>
      {/* On-site cookie notice; content is locale-aware */}
      <Button href="/cookies" theme="invisible" className={styles.link}>
        <Copy.Simple
          id="app_tabs_monzo_me.footer.cookie_notice"
          fallback="Cookie notice"
        />
      </Button>
      <Button href={termsHref} theme="invisible" className={styles.link}>
        <Copy.Simple
          id="app_tabs_monzo_me.footer.terms_and_conditions"
          fallback="Terms & conditions"
        />
      </Button>
    </section>
  )
}

function GbrFooter() {
  return (
    <>
      <FooterLinks
        privacyHref={`${MONZO_COM}/legal/privacy-notice/`}
        termsHref={`${MONZO_COM}/legal/terms-and-conditions/`}
      />
      <Text color="white" className={styles.text}>
        <Copy.Simple
          id="app_tabs_monzo_me.footer.legal.gbr"
          fallback="Monzo Bank Limited is a company registered in England and Wales (No.09446231). Monzo Bank Limited is authorised by the Prudential Regulation Authority and regulated by the Financial Conduct Authority and the Prudential Regulation Authority. Our financial Services Register number is 730427. Our address is Broadwalk House, 5 Appold St, London EC2A 2AG."
        />
      </Text>
    </>
  )
}

function IrlFooter() {
  return (
    <>
      <FooterLinks
        privacyHref={`${MONZO_COM}/ie/legal/privacy-notice`}
        termsHref={`${MONZO_COM}/ie/legal/terms-and-conditions/`}
      />
      <Text color="white" className={styles.text}>
        <Copy.Simple
          id="app_tabs_monzo_me.footer.legal.irl"
          fallback="Monzo Bank Europe Designated Activity Company is a private company limited by shares registered in Ireland (registration no. 770591). Registered office: Unit 1 - Floor 4, One Central Plaza Dame Street Dublin 2 D02 K7K5, Ireland. Monzo Bank Europe Designated Activity Company, trading as Monzo, is regulated by the Central Bank of Ireland."
        />
      </Text>
    </>
  )
}

function EspFooter() {
  const { resolvedLocale } = useLocale()
  const isSpanish = resolvedLocale.localeCode === 'es-ES'

  return (
    <>
      <FooterLinks
        privacyHref={
          isSpanish
            ? `${MONZO_COM}/es/legal/declaracion-de-privacidad`
            : `${MONZO_COM}/en-es/legal/privacy-notice`
        }
        termsHref={
          isSpanish
            ? `${MONZO_COM}/es/legal/terminos-y-condiciones`
            : `${MONZO_COM}/en-es/legal/terms-and-conditions/`
        }
      />
      <Text color="white" className={styles.text}>
        <Copy.Simple
          id="app_tabs_monzo_me.footer.legal.esp"
          fallback="Monzo Bank Europe Designated Activity Company, Sucursal en España, with Tax ID (NIF) W0371919B, is a permanent establishment in Spain of the Irish entity Monzo Bank Europe Designated Activity Company, registered at the Banco de España with entity number 1590, and registered with the Commercial Registry of Madrid in electronic folio, IRUS 1000450787573, Sheet M 854143, Registration 1 and with its registered address at Paseo de la Castellana 43, 28046, Madrid, Spain."
        />
      </Text>
    </>
  )
}

function FooterForCountry({
  country,
}: {
  country: LiveRegionCodesAlpha3 | null
}) {
  switch (country) {
    case 'IRL':
      return <IrlFooter />
    case 'ESP':
      return <EspFooter />
    case 'GBR':
    default:
      return <GbrFooter />
  }
}

export default function Footer({ forCountry }: FooterProps) {
  return (
    <footer className={styles.footer}>
      <div className={styles.inner}>
        <FooterForCountry country={forCountry} />
        <section className={styles.download}>
          <AppStoreButton href="https://monzo.com/download" store="app-store" />
          <AppStoreButton
            href="https://monzo.com/download"
            store="google-play"
          />
        </section>
      </div>
    </footer>
  )
}
